import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { User, AuthState } from '../types/auth.types';

interface AuthContextType extends AuthState {
  getUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  const getUser = async (): Promise<User | null> => {
    try {
      const userData = await Auth.currentAuthenticatedUser();
      return userData;
    } catch (error) {
      console.log('Not signed in');
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const userData = await getUser();
      setUser(userData);
      if (userData) {
        setUsername(userData.signInUserSession.idToken.payload.given_name);
        setEmail(userData.signInUserSession.idToken.payload.email);
      }
    };

    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          getUser().then(userData => {
            setUser(userData);
            if (userData) {
              setUsername(userData.signInUserSession.idToken.payload.given_name);
              setEmail(userData.signInUserSession.idToken.payload.email);
            }
          });
          break;
        case 'signOut':
          setUser(null);
          setUsername('');
          setEmail('');
          break;
        case 'signIn_failure':
          console.log('Sign in failure', data);
          break;
      }
    });

    initAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, username, email, getUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};